<script setup>
import { ref, watch, computed } from 'vue';
import { Link } from '@inertiajs/inertia-vue3';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import Tag from 'primevue/tag';
import axios from 'axios';
import { useToast } from 'primevue/usetoast';
import { useConfirm } from 'primevue/useconfirm';

const props = defineProps({
  visible: { type: Boolean, required: true },
  booking: { type: Object, default: null },
});

const emit = defineEmits(['update:visible', 'edit', 'cancelled', 'refresh']);

const show = ref(props.visible);
const localBooking = ref(props.booking);
const loading = ref(false);
const toast = useToast();
const confirm = useConfirm();

const fetchBooking = async (uuid) => {
    if (!uuid) return;
    loading.value = true;
    try {
        const { data } = await axios.get(`/ajax/admin/bookings/${uuid}`);
        localBooking.value = data.data;
    } catch (e) {
        console.error('Failed to fetch booking details:', e);
    } finally {
        loading.value = false;
    }
};

// Consolidated watcher to prevent double API calls
watch(
    () => [props.visible, props.booking?.uuid || props.booking?.id],
    (newVal, oldVal) => {
        const [isVisible, uuid] = newVal;
        const [oldVisible, oldUuid] = oldVal || [false, null];
        
        show.value = isVisible;
        
        if (isVisible && uuid) {
            // Only fetch if visibility changed to true OR a different booking was selected
            if (!oldVisible || uuid !== oldUuid) {
                localBooking.value = props.booking;
                fetchBooking(uuid);
            }
        }
    },
    { immediate: true }
);

  watch(show, (v) => {
    emit('update:visible', v);
  });

const formatTime = (dt) => {
  if (!dt) return '--:--';
  if (typeof dt === 'string' && dt.includes(' ')) {
    const timePart = dt.split(' ')[1];
    return timePart.substring(0, 5);
  }
  return new Date(dt).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
};

const formatDate = (dt) => {
  if (!dt) return '';
  if (typeof dt === 'string' && dt.includes(' ')) {
     const [y, m, d] = dt.split(' ')[0].split('-');
     return `${d}.${m}.${y}`;
  }
  return new Date(dt).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const start = computed(() => localBooking.value?.start_datetime);
const end = computed(() => localBooking.value?.end_datetime);
const blockedStart = computed(() => localBooking.value?.blocked_start_datetime);
const blockedEnd = computed(() => localBooking.value?.blocked_end_datetime);

const duration = computed(() => localBooking.value?.duration || 0);
const bufferBefore = computed(() => localBooking.value?.buffer_before_minutes || 0);
const bufferAfter = computed(() => localBooking.value?.buffer_after_minutes || 0);

const statusText = computed(() => {
  return localBooking.value?.status?.toUpperCase() || 'PENDING';
});

const statusClasses = computed(() => {
  const s = localBooking.value?.status?.toLowerCase() || '';
  if (s === 'ready') return '!bg-green-100 !text-green-700';
  if (s === 'confirmed') return '!bg-sky-100 !text-sky-700';
  if (s === 'completed') return '!bg-gray-800 !text-white';
  if (s === 'pending') return '!bg-orange-100 !text-orange-700';
  if (['cancelled_by_customer', 'cancelled_by_staff', 'no_show'].includes(s)) return '!bg-red-100 !text-red-700';
  return '!bg-gray-100 !text-gray-700';
});

const isCancelled = computed(() => {
  const s = localBooking.value?.status?.toLowerCase() || '';
  return ['cancelled_by_customer', 'cancelled_by_staff'].includes(s);
});

const isNoShow = computed(() => localBooking.value?.status?.toLowerCase() === 'no_show');

const extractError = (e, defaultMsg = 'Operation failed') => {
    const data = e.response?.data;
    if (data?.errors) {
        const first = Object.values(data.errors).flat()[0];
        if (first) return first;
    }
    return data?.message || e.message || defaultMsg;
};

const cancelBooking = async () => {
    confirm.require({
        message: 'Möchten Sie diese Buchung wirklich stornieren?',
        header: 'Stornierung bestätigen',
        icon: 'pi pi-exclamation-triangle',
        rejectLabel: 'Nein, behalten',
        acceptLabel: 'Ja, stornieren',
        rejectClass: 'p-button-secondary p-button-outlined',
        acceptClass: 'p-button-danger',
        accept: async () => {
            try {
                await axios.post(`/ajax/admin/bookings/${localBooking.value.uuid}/cancel`, {
                    cancelled_by: 'staff'
                });
                toast.add({ severity: 'success', summary: 'Erfolg', detail: 'Buchung erfolgreich storniert', life: 3000 });
                emit('cancelled');
                show.value = false;
            } catch (e) {
                toast.add({ severity: 'error', summary: 'Fehler', detail: extractError(e, 'Stornierung fehlgeschlagen'), life: 5000 });
            }
        }
    });
};

const customerName = computed(() => {
  const c = localBooking.value?.customer;
  if (!c) return 'Kunde';
  return `${c.first_name || ''} ${c.last_name || ''}`.trim() || 'Kunde';
});

// Direct link to the customer's profile (training-plan page = customer landing)
const customerProfileUrl = computed(() => {
  const uuid = localBooking.value?.customer?.uuid;
  return uuid ? `/customers/${uuid}/trainingsplan` : null;
});

const resourceDisplay = computed(() => {
    const reservations = localBooking.value?.resource_reservations;
    if (!reservations || reservations.length === 0) {
        return 'Standard Capacity';
    }
    
    return reservations.map(res => {
        const name = res.device_resource?.name || res.device?.name || 'Device';
        return res.quantity > 1 ? `${name} x${res.quantity}` : name;
    }).join(', ');
});

const showStatusModal = ref(false);
const newStatus = ref('');
const isUpdatingStatus = ref(false);
const statusOptions = ref([
  { label: 'Pending', value: 'pending' },
  { label: 'Confirmed', value: 'confirmed' },
  { label: 'Ready', value: 'ready' },
  { label: 'Completed', value: 'completed' },
  { label: 'No Show', value: 'no_show' },
]);

const openStatusModal = () => {
  newStatus.value = localBooking.value?.status?.toLowerCase() || 'pending';
  showStatusModal.value = true;
};

const saveStatus = async () => {
    isUpdatingStatus.value = true;
    try {
        await axios.patch(`/ajax/admin/bookings/${localBooking.value.uuid}/status`, {
            status: newStatus.value
        });
        toast.add({ severity: 'success', summary: 'Erfolg', detail: 'Status erfolgreich aktualisiert', life: 3000 });
        showStatusModal.value = false;
        fetchBooking(localBooking.value.uuid);
        emit('refresh');
    } catch (e) {
        toast.add({ severity: 'error', summary: 'Fehler', detail: extractError(e, 'Status Update fehlgeschlagen'), life: 3000 });
    } finally {
        isUpdatingStatus.value = false;
    }
};
</script>

<template>
  <Dialog 
    v-model:visible="show" 
    modal 
    header="Buchungsdetails" 
    :closable="true"
    class="booking-detail-dialog"
    :style="{ width: '500px' }"
    contentClass="!p-8 !pt-4"
  >
    <!-- Skeleton/Loading state -->
    <div v-if="loading && !localBooking" class="flex flex-col gap-4 py-6">
       <div class="h-6 w-3/4 bg-gray-100 animate-pulse rounded"></div>
       <div class="h-20 w-full bg-gray-50 animate-pulse rounded-xl"></div>
       <div class="h-20 w-full bg-gray-50 animate-pulse rounded-xl"></div>
    </div>
    
    <div v-else-if="localBooking" class="space-y-8 pt-2">
      <!-- Service & Status -->
      <div class="flex justify-between items-start">
        <div>
          <h2 class="text-xl font-bold text-gray-900 leading-tight tracking-tight">{{ localBooking?.service?.name || 'Cryo Session' }}</h2>
          <div class="text-[10px] text-gray-400 font-bold mt-1">Dienstleistung</div>
        </div>
        <div class="flex gap-2 items-center">
            <Tag v-if="localBooking?.is_rescheduled" value="VERSCHOBEN" severity="info" class="!text-[9px] !px-2 !py-0.5 !font-black !rounded !bg-gray-200 !text-gray-600" />
            <Tag :value="statusText" class="!text-[9px] !px-2 !py-0.5 !font-black !rounded" :class="statusClasses" />
        </div>
      </div>

      <!-- Resource Grid -->
      <div class="grid grid-cols-2 gap-8">
        <div class="space-y-2">
          <div class="text-[10px] font-bold text-gray-400">Mitarbeiter</div>
          <div class="flex items-center gap-3">
            <div class="w-9 h-9 rounded-xl bg-gray-50 border border-gray-100 flex items-center justify-center text-gray-400">
              <i class="pi pi-user text-[12px]"></i>
            </div>
            <span class="text-[13px] font-bold text-gray-700 tracking-tight">{{ localBooking?.provider?.name || 'team' }}</span>
          </div>
        </div>
        <div class="space-y-2">
          <div class="text-[10px] font-bold text-gray-400">Ressource</div>
          <div class="flex items-center gap-3">
            <div class="w-9 h-9 rounded-xl bg-gray-50 border border-gray-100 flex items-center justify-center text-gray-400">
              <i class="pi pi-box text-[12px]"></i>
            </div>
            <span class="text-[13px] font-bold text-gray-700 tracking-tight">{{ resourceDisplay }}</span>
          </div>
        </div>
      </div>

      <!-- Time Section -->
      <div class="bg-gray-50/50 rounded-3xl p-6 border border-gray-100 space-y-5">
        <div class="flex items-center gap-2 text-[10px] font-bold text-gray-400">
           <i class="pi pi-clock text-[11px]"></i>
           Termindetails
        </div>
        <div class="grid grid-cols-2 gap-y-6 gap-x-8">
          <div>
            <div class="text-[10px] text-gray-500 font-bold mb-1">Servicezeit</div>
            <div class="text-[14px] font-bold text-indigo-600 tracking-tighter">{{ formatTime(start) }} - {{ formatTime(end) }}</div>
          </div>
          <div>
            <div class="text-[10px] text-gray-500 font-bold mb-1">Gesamt (Blockiert)</div>
            <div class="text-[14px] font-bold text-gray-900 tracking-tighter">{{ formatTime(blockedStart || start) }} - {{ formatTime(blockedEnd || end) }}</div>
          </div>
          <div>
            <div class="text-[10px] text-gray-500 font-bold mb-1">Dauer</div>
            <div class="text-[13px] font-bold text-gray-700 tracking-tight">{{ duration }} min.</div>
          </div>
          <div v-if="bufferBefore > 0 || bufferAfter > 0">
            <div class="text-[10px] text-gray-500 font-bold mb-1">Puffer</div>
            <div class="text-[13px] font-bold text-gray-700 tracking-tight">
              <span v-if="bufferBefore > 0">+ {{ bufferBefore }}m davor</span>
              <span v-if="bufferBefore > 0 && bufferAfter > 0" class="mx-1 text-gray-300">|</span>
              <span v-if="bufferAfter > 0">+ {{ bufferAfter }}m danach</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Customer -->
      <div class="space-y-4">
        <div class="text-[10px] font-bold text-gray-400">Kunde</div>
        <div class="flex items-center gap-4">
          <div class="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-400">
            <i class="pi pi-user text-sm"></i>
          </div>
          <div>
            <div class="text-[14px] font-bold text-gray-900 tracking-tight">{{ customerName }}</div>
            <div class="text-[11px] text-gray-500 font-bold">{{ localBooking?.customer?.email || 'Keine E-Mail' }}</div>
            <div v-if="localBooking?.customer?.phone" class="text-[11px] text-gray-500 font-bold mt-0.5">
               {{ localBooking.customer.phone }}
            </div>
          </div>
          <Link
            v-if="customerProfileUrl"
            :href="customerProfileUrl"
            class="ml-auto inline-flex items-center gap-1.5 text-[11px] font-bold text-indigo-600 border border-indigo-200 rounded-xl px-3 py-2 hover:bg-indigo-50 transition-all whitespace-nowrap"
            title="Kundenprofil öffnen"
          >
            <i class="pi pi-external-link text-[11px]"></i>
            Kundenprofil
          </Link>
        </div>
        
        <div v-if="localBooking?.customer_notes" class="mt-2 text-[11px] text-gray-600 italic bg-yellow-50/30 p-4 rounded-2xl border border-yellow-100/50 leading-relaxed">
           "{{ localBooking?.customer_notes }}"
        </div>

        <!-- Meta Info: Created By -->
        <div class="pt-4 border-t border-gray-50 flex flex-wrap gap-x-6 gap-y-2">
           <div class="text-[9px] font-bold text-gray-400">
              Erstellt von: <span class="text-gray-600 ml-1">{{ localBooking?.created_by_user?.email || 'System' }}</span>
           </div>
           <div class="text-[9px] font-bold text-gray-400">
              Erstellt am: <span class="text-gray-600 ml-1">{{ formatDate(localBooking?.created_at) }} {{ formatTime(localBooking?.created_at) }}</span>
           </div>
        </div>
      </div>

      <!-- Footer Actions -->
      <div class="flex items-center justify-between pt-8 border-t border-gray-100">
        <button 
          type="button"
          @click="show = false" 
          class="text-gray-500 font-bold hover:!bg-sky-50 hover:!text-sky-600 transition-all px-6 py-2 rounded-full text-[11px]"
        >
          Schließen
        </button>
        <div v-if="!isCancelled" class="flex items-center gap-4">
          <Button
            v-if="!isNoShow"
            @click="cancelBooking"
            label="Stornieren"
            text
            class="!text-red-500 !text-[11px] !font-bold !border !border-red-500 !rounded-xl !px-6 !py-2.5 hover:!bg-red-50/50 transition-all"
          />
          <Button 
            @click="openStatusModal"
            label="Status ändern" 
            class="!bg-indigo-50 !text-indigo-600 !border !border-indigo-200 !rounded-xl !px-6 !py-2.5 !text-[11px] !font-bold hover:!bg-indigo-100 transition-all"
          />
          <Button 
            @click="emit('edit', localBooking)"
            label="Bearbeiten" 
            class="!bg-black !text-white !border-none !rounded-xl !px-8 !py-3 !text-[11px] !font-bold shadow-xl shadow-black/10 active:scale-95 transition-all"
          />
        </div>
      </div>
    </div>
  </Dialog>

  <!-- Status Update Modal -->
  <Dialog 
    v-model:visible="showStatusModal" 
    modal 
    header="Status ändern" 
    :style="{ width: '400px' }"
    class="booking-detail-dialog"
    contentClass="!p-6 !pt-2"
  >
    <div class="flex flex-col gap-6">
      <div class="flex flex-col gap-2">
        <label class="font-bold text-gray-500 text-[10px] uppercase tracking-widest">Neuer Status</label>
        <div class="flex flex-wrap gap-2">
          <button 
            v-for="opt in statusOptions" 
            :key="opt.value"
            @click="newStatus = opt.value"
            class="px-4 py-2 rounded-xl text-[12px] font-bold border transition-all"
            :class="[
              newStatus === opt.value 
                ? 'bg-black text-white border-black shadow-md' 
                : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300 hover:bg-gray-50'
            ]"
          >
            {{ opt.label }}
          </button>
        </div>
      </div>
      
      <div class="flex gap-4 pt-4 border-t border-gray-100">
        <button 
          type="button"
          @click="showStatusModal = false" 
          class="text-gray-400 font-bold hover:!bg-sky-50 hover:!text-sky-600 transition-all px-4 py-2 rounded-full flex-1 uppercase tracking-tight text-[11px]"
        >
          Abbrechen
        </button>
        <Button 
          @click="saveStatus" 
          label="Speichern" 
          :loading="isUpdatingStatus"
          class="!rounded-xl !px-6 !py-2.5 !font-bold !bg-black !text-white !border-none flex-1 shadow-md hover:!scale-[1.02] active:!scale-[0.98] transition-all uppercase tracking-widest text-[11px]" 
        />
      </div>
    </div>
  </Dialog>
</template>

<style>
.booking-detail-dialog .p-dialog-header {
  border-bottom: 1px solid #f3f4f6;
  padding: 1.5rem 2rem;
}
.booking-detail-dialog .p-dialog-title {
  font-size: 0.85rem;
  font-weight: 700;
  color: #6b7280;
}
.booking-detail-dialog .p-dialog-content {
  padding: 0;
}
</style>
